// @spec SHL-STR-005
'use client';

import { Link, createFileRoute } from '@tanstack/react-router';
import { Building2, Calendar, MonitorSmartphone, ShieldCheck, User, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useOrgRole } from '@/hooks/use-org-role';
import i18n from '@/lib/i18n';

interface SettingsLink {
  to: string;
  title: string;
  description: string;
  icon: typeof Building2;
}

export const Route = createFileRoute('/_authenticated/settings/')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.settings'),
  }),
  component: SettingsIndexPage,
});

function SettingsIndexPage() {
  const { isAdmin, isAdminOrManager } = useOrgRole();
  const { t } = useTranslation('settings');

  const links: SettingsLink[] = [
    {
      to: '/settings/profile',
      title: t('index.profile'),
      description: t('index.profileDescription'),
      icon: User,
    },
    {
      to: '/settings/security',
      title: t('index.security'),
      description: t('index.securityDescription'),
      icon: MonitorSmartphone,
    },
    {
      to: '/settings/organizations',
      title: t('index.organizations'),
      description: t('index.organizationsDescription'),
      icon: Building2,
    },
  ];

  if (isAdminOrManager) {
    links.push({
      to: '/settings/team',
      title: t('index.team'),
      description: t('index.teamDescription'),
      icon: Users,
    });
    links.push({
      to: '/settings/roles',
      title: t('index.rolesAndPermissions'),
      description: t('index.rolesAndPermissionsDescription'),
      icon: ShieldCheck,
    });
  }

  if (isAdmin) {
    links.push({
      to: '/settings/organisation-structure',
      title: t('index.organisationStructure'),
      description: t('index.organisationStructureDescription'),
      icon: Building2,
    });
    links.push({
      to: '/settings/reporting-periods',
      title: t('index.reportingPeriods'),
      description: t('index.reportingPeriodsDescription'),
      icon: Calendar,
    });
  }

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-6xl space-y-8">
        <header>
          <h1 className="text-3xl font-semibold text-foreground">{t('index.title')}</h1>
          <p className="mt-1 text-muted-foreground">
            {t('index.subtitle')}
          </p>
        </header>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {links.map((item) => {
            const Icon = item.icon;
            return (
              <Link key={item.to} to={item.to}>
                <Card className="h-full transition-colors hover:border-primary/40">
                  <CardHeader>
                    <div className="mb-2 inline-flex h-9 w-9 items-center justify-center rounded-md bg-muted">
                      <Icon className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <CardTitle>{item.title}</CardTitle>
                    <CardDescription>{item.description}</CardDescription>
                  </CardHeader>
                </Card>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
